import React, { useMemo, useState, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, FileText } from 'lucide-react';
import WorkPassFieldGroup from './WorkPassFieldGroup';
import {
  WORK_PASS_APPLICATION_SCHEMA,
  ALL_WORK_PASS_FIELDS,
  REQUIRED_FIELD_KEYS,
} from './workPassApplicationSchema';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isEmpty = (v) => v === null || v === undefined || String(v).trim() === '';

/**
 * Pure validator — returns a map of fieldKey -> message for invalid fields.
 * Exported so callers can validate before an API round-trip if they wish.
 */
export const validateWorkPassValues = (values = {}, schema = WORK_PASS_APPLICATION_SCHEMA) => {
  const fields = schema.flatMap((s) => s.fields);
  const errors = {};
  fields.forEach((f) => {
    const value = values[f.key];
    if (f.required && isEmpty(value)) {
      errors[f.key] = 'Required';
      return;
    }
    if (!isEmpty(value) && f.type === 'email' && !EMAIL_RE.test(String(value).trim())) {
      errors[f.key] = 'Enter a valid email address';
    }
  });
  return errors;
};

const Skeleton = () => (
  <div className="space-y-4" aria-hidden="true">
    {[0, 1].map((s) => (
      <div key={s} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 h-5 w-44 animate-pulse rounded bg-gray-200" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i}>
              <div className="mb-2 h-3 w-24 animate-pulse rounded bg-gray-200" />
              <div className="h-11 animate-pulse rounded-xl bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

/**
 * WorkPassApplicationForm — orchestrates the full Work Pass Application (Form B).
 *
 * Schema-driven, theme-aware, mode-aware, and backend-agnostic. The same
 * component serves the HR review panel (admin theme, read-only with prefill
 * badges + gap summary) and the candidate fill experience (employee theme,
 * locked prefills + editable gaps + validated submit).
 *
 * Modes:
 *   'candidate'  → editable; prefilled fields locked; submit with validation.
 *   'hr-review'  → read-only; shows prefilled badges and a "candidate still
 *                  needs to complete N fields" summary; use `footer` for the
 *                  Send / Resend action.
 *   'readonly'   → plain read-only display (e.g. after submission / on hire).
 *
 * @param {object[]} [schema]          defaults to the built-in Form B schema
 * @param {object}   values            map of fieldKey -> value (controlled)
 * @param {function} [onChange]        (key, value) => void  (candidate mode)
 * @param {function} [onSubmit]        (values) => void|Promise  (candidate mode)
 * @param {'candidate'|'hr-review'|'readonly'} [mode]
 * @param {Set|object} [prefilledKeys] keys prefilled from the ATS handoff
 * @param {object}   [errors]          external/server errors (merged over local)
 * @param {boolean}  [loading]         show skeleton
 * @param {boolean}  [submitting]      disable controls + show submitting state
 * @param {boolean}  [lockPrefilled]   lock prefilled fields (default true in candidate mode)
 * @param {'admin'|'employee'} [theme] default 'employee'
 * @param {string}   [submitLabel]     candidate submit button text
 * @param {React.ReactNode} [footer]   custom footer actions (e.g. HR Send button)
 * @param {string}   [className]
 */
const WorkPassApplicationForm = ({
  schema = WORK_PASS_APPLICATION_SCHEMA,
  values = {},
  onChange,
  onSubmit,
  mode = 'candidate',
  prefilledKeys,
  errors: externalErrors,
  loading = false,
  submitting = false,
  lockPrefilled,
  theme = 'employee',
  submitLabel = 'Submit application',
  footer,
  className = '',
}) => {
  const isAdmin = theme === 'admin';
  const editable = mode === 'candidate';
  const effectiveLockPrefilled = lockPrefilled ?? mode === 'candidate';
  const [localErrors, setLocalErrors] = useState({});

  const mergedErrors = useMemo(
    () => ({ ...localErrors, ...(externalErrors || {}) }),
    [localErrors, externalErrors]
  );

  // Completion stats over required fields (drives candidate progress + HR gap summary).
  const { filled, total, missingFields } = useMemo(() => {
    const requiredFields = ALL_WORK_PASS_FIELDS.filter((f) => REQUIRED_FIELD_KEYS.includes(f.key));
    const missing = requiredFields.filter((f) => isEmpty(values[f.key]));
    return {
      filled: requiredFields.length - missing.length,
      total: requiredFields.length,
      missingFields: missing,
    };
  }, [values]);

  const pct = total > 0 ? Math.round((filled / total) * 100) : 0;

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (submitting) return;
      const validation = validateWorkPassValues(values, schema);
      setLocalErrors(validation);
      if (Object.keys(validation).length > 0) {
        // Move focus to the first invalid field for keyboard/AT users.
        const firstKey = Object.keys(validation)[0];
        const el = document.getElementById(`wpf-${firstKey}`);
        if (el && typeof el.focus === 'function') el.focus();
        return;
      }
      onSubmit?.(values);
    },
    [values, schema, submitting, onSubmit]
  );

  const handleChange = useCallback(
    (key, value) => {
      // Clear a field's local error as soon as the user edits it.
      setLocalErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      onChange?.(key, value);
    },
    [onChange]
  );

  if (loading) return <Skeleton />;

  if (!Array.isArray(schema) || schema.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center">
        <FileText className="mx-auto mb-3 h-10 w-10 text-gray-300" aria-hidden="true" />
        <p className="text-sm text-gray-500">No application fields are configured.</p>
      </div>
    );
  }

  const Wrapper = editable ? 'form' : 'div';
  const wrapperProps = editable ? { onSubmit: handleSubmit, noValidate: true } : {};

  return (
    <Wrapper {...wrapperProps} className={`space-y-4 ${className}`.trim()}>
      {/* Candidate progress */}
      {mode === 'candidate' && total > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-gray-700">Your progress</span>
            <span className="text-xs text-gray-500">
              {filled} of {total} required fields
            </span>
          </div>
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Required fields completed"
          >
            <div
              className={`h-2 rounded-full transition-all ${isAdmin ? 'bg-red-600' : 'bg-blue-600'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      {/* HR review — gap summary */}
      {mode === 'hr-review' && (
        <div
          className={`rounded-2xl border p-4 ${
            missingFields.length === 0
              ? 'border-green-200 bg-green-50'
              : 'border-amber-200 bg-amber-50'
          }`}
        >
          {missingFields.length === 0 ? (
            <div className="flex items-center gap-2 text-sm font-medium text-green-800">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              All required fields are complete.
            </div>
          ) : (
            <div className="text-sm text-amber-900">
              <div className="mb-1 flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                Candidate still needs to complete {missingFields.length} field
                {missingFields.length > 1 ? 's' : ''}
              </div>
              <p className="text-xs text-amber-800">
                {missingFields.map((f) => f.label).join(', ')}
              </p>
            </div>
          )}
        </div>
      )}

      {schema.map((section) => (
        <WorkPassFieldGroup
          key={section.key}
          section={section}
          values={values}
          onChange={handleChange}
          errors={mergedErrors}
          prefilledKeys={prefilledKeys}
          editable={editable}
          lockPrefilled={effectiveLockPrefilled}
          disabled={submitting}
          theme={theme}
        />
      ))}

      {/* Footer / actions */}
      {footer ? (
        <div className="flex flex-wrap items-center justify-end gap-2">{footer}</div>
      ) : (
        mode === 'candidate' && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            {Object.keys(mergedErrors).length > 0 && (
              <p role="alert" className="mr-auto text-xs text-red-600">
                Please correct the highlighted fields before submitting.
              </p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className={`btn ${isAdmin ? 'btn-admin-primary' : 'btn-primary'} justify-center`}
            >
              {submitting ? 'Submitting…' : submitLabel}
            </button>
          </div>
        )
      )}
    </Wrapper>
  );
};

export default WorkPassApplicationForm;
